import { ServerMessagePayload } from '@common/api';
import { CommonRegistry } from '@common/components/components';
import { World } from '@common/World';

import { ClientRegistry } from '../components';
import { SpritesheetService } from '../services/SpritesheetService';
import {
  ClientFilteredComponentSystem,
  FilteredClientComponentSystemUpdateEntityArgs,
} from './ClientBase';

/**
 * Advances animation frames based on time and spritesheet metadata.
 *
 * Requires both `Animation` and `SpritesheetSprite` components. The `Animation`
 * component is typically created by selection systems (IdleAnimationSystem,
 * WalkingAnimationSystem) based on entity state, not at entity creation time.
 *
 * Until an `Animation` component is attached, this system won't run for the
 * entity and the initial SpritesheetSprite.frameIndex (default: 1) is used.
 */
export class AnimationSystem extends ClientFilteredComponentSystem {
  public componentClasses: (keyof ClientRegistry)[] = ['Animation', 'SpritesheetSprite'];

  updateEntity({
    world,
    entityId,
    deltaTime,
  }: FilteredClientComponentSystemUpdateEntityArgs): ServerMessagePayload[] {
    const animation = world.getComponent(entityId, 'Animation');
    const spritesheetSprite = world.getComponent(entityId, 'SpritesheetSprite');
    const spritesheetService = this.clientServices.spritesheet;

    if (!animation || !spritesheetSprite || !animation.isPlaying) {
      return [];
    }

    // Check if dead - stop standard animations (idle, walk, run)
    // but allow others (flies, death effects)
    const dead = world.getComponent(entityId, 'Dead');
    if (dead) {
      if (
        animation.name.startsWith('idle') ||
        animation.name.startsWith('walk') ||
        animation.name.startsWith('run')
      ) {
        animation.isPlaying = false;
        return [];
      }
    }

    // Get animation metadata from spritesheet service
    const animationData = spritesheetService.getAnimation(
      spritesheetSprite.spritesheetId,
      animation.name,
    );

    if (!animationData) {
      // Animation not found - keep current frame
      // This can happen if spritesheet isn't loaded yet
      return [];
    }

    // Calculate current sheet frame from animation frame
    const currentSheetFrame = animationData.fromFrame + animation.animationFrame - 1;

    // Initialize or recalculate frame duration if needed
    if (animation.currentFrameDurationMs === undefined) {
      animation.currentFrameDurationMs = this.calculateFrameDuration(
        spritesheetService,
        spritesheetSprite.spritesheetId,
        currentSheetFrame,
      );
    }

    const speedScale = this.clientServices.common.speed.getAnimationSpeedScale(
      world as unknown as World<CommonRegistry>,
      entityId,
    );
    animation.timer += deltaTime * 1000 * speedScale; // Convert to ms, scaled by speed

    if (animation.timer >= animation.currentFrameDurationMs) {
      animation.timer -= animation.currentFrameDurationMs;
      animation.animationFrame++;

      // Check if we've exceeded the animation's frame count
      if (animation.animationFrame > animationData.frameCount) {
        if (animation.loop) {
          animation.animationFrame = 1;
        } else {
          animation.animationFrame = animationData.frameCount;
          animation.isPlaying = false;
        }
      }

      // Recalculate sheet frame and duration for the new animation frame
      const newSheetFrame = animationData.fromFrame + animation.animationFrame - 1;
      animation.currentFrameDurationMs = this.calculateFrameDuration(
        spritesheetService,
        spritesheetSprite.spritesheetId,
        newSheetFrame,
      );

      // Update SpritesheetSprite.frameIndex
      spritesheetSprite.frameIndex = newSheetFrame;
    } else if (spritesheetSprite.frameIndex !== currentSheetFrame) {
      // Ensure frameIndex is correct even if timer hasn't advanced
      spritesheetSprite.frameIndex = currentSheetFrame;
    }

    return [];
  }

  /**
   * Calculate frame duration, using random value if frame has a duration range.
   */
  private calculateFrameDuration(
    spritesheetService: SpritesheetService,
    spritesheetId: string,
    sheetFrame: number,
  ): number {
    const range = spritesheetService.getFrameDurationRange(spritesheetId, sheetFrame);
    const sheet = spritesheetService.getSpritesheet(spritesheetId);
    const baseMs = sheet?.baseFrameDurationMs ?? spritesheetService.getDefaultFrameDurationMs();

    if (range) {
      // Skip randomization if range has no variability
      if (range.min === range.max) {
        return baseMs * range.min;
      }
      // Random duration within range
      const multiplier = range.min + Math.random() * (range.max - range.min);
      return baseMs * multiplier;
    }

    // Use standard frame duration
    return spritesheetService.getFrameDurationMs(spritesheetId, sheetFrame);
  }
}
